import { Fraction } from 'bi-fraction';
import { JsonRpcSigner } from 'ethers';
import { L1Signer, utils } from 'zksync-ethers';
import { estimateDefaultBridgeDepositL2Gas } from 'zksync-ethers/build-esm/utils';
import { BridgeMiddleware as BridgeMiddlewareAbi, ERC20 as ERC20Abi, Zkcro as ZkcroAbi, } from '../abis';
import { ZKCRO_DECIMALS } from '../config/constants';
import { ChainLayer, FromBalanceTooLowError as FromBalanceTooLowError, } from '../types';
import { getContract, getProvider } from '../utils';
// TODO: not tested
export class ZkCroToZkCroDepositor {
    constructor(options) {
        this.options = options;
        this.zkCroContract = this.createZkCroContract();
        this.middleware = this.createMiddlewareContract();
        this.l1Signer =
            options.singerOrProvider instanceof JsonRpcSigner
                ? L1Signer.from(options.singerOrProvider, getProvider(options.l2ProviderOptions, ChainLayer.L2))
                : null;
    }
    /**
     * Deposits the given amount of tokens to L2 via the YbTokenBridge (YbEthBridge or YbUsdBridge) contract.
     * Will execute approval if needed.
     *
     * @param croAmount cro amount to be deposited
     * @returns receipt of the deposit transaction
     * @throws if the CRO balance is too low
     */
    async depositZkCro(zkCroAmount) {
        if (!this.l1Signer) {
            throw new Error('singerOrProvider should be JsonRpcSigner');
        }
        const amountOfZkCroToBridge = zkCroAmount.shl(ZKCRO_DECIMALS).quotient;
        const [l2GasFee, zkCroBalance] = await Promise.all([
            this.getL2GasFee(zkCroAmount),
            this.zkCroContract.erc20.balanceOf(this.options.depositorAddress),
        ]);
        const totalZkCroNeeded = amountOfZkCroToBridge + l2GasFee;
        if (totalZkCroNeeded > zkCroBalance) {
            throw new FromBalanceTooLowError(new Fraction(totalZkCroNeeded - zkCroBalance).shr(ZKCRO_DECIMALS));
        }
        const priorityOpResponse = await this.l1Signer.deposit({
            to: this.options.depositorAddress,
            token: this.options.contractAddressMap.ZkCRO,
            amount: amountOfZkCroToBridge,
            approveERC20: true,
            approveBaseERC20: true,
        });
        await priorityOpResponse.waitL1Commit();
        return priorityOpResponse;
    }
    async getL2GasFee(zkCroAmount) {
        const amountOfZkCroToBridge = zkCroAmount.shl(ZKCRO_DECIMALS).quotient;
        const [l2GasLimit, l1GasPrice] = await Promise.all([
            estimateDefaultBridgeDepositL2Gas(getProvider(this.options.l1ProviderOptions, ChainLayer.L1), 
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            getProvider(this.options.l2ProviderOptions, ChainLayer.L2), this.options.contractAddressMap.ZkCRO, amountOfZkCroToBridge, this.options.depositorAddress).then(BigInt),
            getProvider(this.options.l1ProviderOptions, ChainLayer.L1)
                .getFeeData()
                .then((feeData) => feeData.maxFeePerGas),
        ]);
        const l2GasPerPubdataByteLimit = utils.REQUIRED_L1_TO_L2_GAS_PER_PUBDATA_LIMIT;
        const l2TxCostInZkCRO = await this.middleware.l2TransactionBaseCost(l1GasPrice ?? 0n, l2GasLimit, l2GasPerPubdataByteLimit);
        // (from article) This value is the sum of cro which the user wanna bridge, plus the gas fee in zkcro, and
        // a little buffer due do conversion error ... so for best experience, use 1 wei at least. 10 wei also safe.
        return l2TxCostInZkCRO + 10n;
    }
    createZkCroContract() {
        return {
            zkcro: getContract(this.options.contractAddressMap.ZkCRO, ZkcroAbi, this.options.singerOrProvider),
            erc20: getContract(this.options.contractAddressMap.ZkCRO, ERC20Abi, this.options.singerOrProvider),
        };
    }
    createMiddlewareContract() {
        return getContract(this.options.contractAddressMap.BridgeMiddleware, BridgeMiddlewareAbi, this.options.singerOrProvider);
    }
}
